import { useCallback, useEffect, useState } from "react";
import { graphqlFetch } from "@/lib/graphql-client";
import type { PaymentProvider } from "@/lib/payment-providers";

export type PublicPaymentAuthorization = {
  accessCode?: string | null;
  authorizationUrl?: string | null;
  clientSecret?: string | null;
  reference?: string | null;
};

export type PublicAppointmentPayment = {
  id: string;
  amount: number;
  totalAmount: number;
  processingFeeAmount?: number | null;
  mode?: string | null;
  meta?: Record<string, unknown> | null;
  paymentType?: string | null;
  createdAt?: string | null;
};

export type PublicAppointmentSummaryAppointment = {
  id: string;
  bookingId: string;
  appointmentStatus: string;
  startAt: string;
  endAt?: string | null;
  address?: string | null;
  locationType?: string | null;
  timezone: string;
  depositAmount: number;
  actualTotalPaid?: number | null;
  totalPaid?: number | null;
  totalPrice?: number | null;
  totalServicesAmount?: number | null;
  totalProductsAmount?: number | null;
  taxAmount?: number | null;
  discountAmount?: number | null;
  tipAmount?: number | null;
  processingFeeAmount?: number | null;
  whoBearsProcessingFee?: string | null;
  payments?: PublicAppointmentPayment[] | null;
  appointmentPromo?: {
    id?: string | null;
    title?: string | null;
    amount?: number | null;
  } | null;
  appointmentServices?: Array<{
    id: string;
    name?: string | null;
    price?: number | null;
    quantity?: number | null;
    duration?: number | null;
    startAt?: string | null;
    endAt?: string | null;
    serviceId?: string | null;
    serviceVariantId?: string | null;
    appointmentServiceStaff?: Array<{
      salonStaff?: {
        id: string;
        user?: {
          firstName?: string | null;
          lastName?: string | null;
          fullName?: string | null;
        } | null;
      } | null;
    }> | null;
  }> | null;
  appointmentProducts?: Array<{
    id: string;
    name?: string | null;
    price?: number | null;
    quantity?: number | null;
    productId?: string | null;
  }> | null;
  client?: {
    id?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone?: string | null;
    callingCode?: string | null;
    countryCode?: string | null;
  } | null;
  salon?: {
    id: string;
    timezone: string;
    branchName?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    email?: string | null;
    phone?: string | null;
    callingCode?: string | null;
    countryCode?: string | null;
    bearsProcessingFee?: boolean | null;
    isTaxVisible?: boolean | null;
    taxInclusivePricing?: boolean | null;
    locationType?: string | null;
    business?: {
      id: string;
      name?: string | null;
      logoUrl?: string | null;
      headerImageUrl?: string | null;
      bookingUrlIdentifier?: string | null;
      cancellationPolicy?: string | null;
      priceHidden?: boolean | null;
      countryCode?: string | null;
      country?: {
        code?: string | null;
        currency?: {
          symbol?: string | null;
          code?: string | null;
          factor?: number | null;
        } | null;
      } | null;
    } | null;
  } | null;
};

export type PublicAppointmentSummary = {
  appointment?: PublicAppointmentSummaryAppointment | null;
  paymentAuthorization?: PublicPaymentAuthorization | null;
  amountToPay?: number | null;
  processingFeeAmount?: number | null;
  purpose?: string | null;
  message?: string | null;
  beautyNowAppUrl?: string | null;
  beautyNowAppCtaImageUrl?: string | null;
};

type PaymentInput = {
  bookingId?: string;
  id?: string;
  email?: string;
  paymentProvider?: PaymentProvider;
  channels?: string[];
};

const APPOINTMENT_FIELDS = `
  appointment {
    id
    bookingId
    appointmentStatus
    startAt
    endAt
    address
    locationType
    timezone
    depositAmount
    actualTotalPaid
    totalPaid
    totalPrice
    totalServicesAmount
    totalProductsAmount
    taxAmount
    discountAmount
    tipAmount
    processingFeeAmount
    whoBearsProcessingFee
    payments {
      id
      amount
      totalAmount
      processingFeeAmount
      mode
      meta
      paymentType
      createdAt
    }
    appointmentPromo {
      id
      title
      amount
    }
    appointmentServices {
      id
      name
      price
      quantity
      duration
      startAt
      endAt
      serviceId
      serviceVariantId
      appointmentServiceStaff {
        salonStaff {
          id
          user {
            firstName
            lastName
            fullName
          }
        }
      }
    }
    appointmentProducts {
      id
      name
      price
      quantity
      productId
    }
    client {
      id
      firstName
      lastName
      email
      phone
      callingCode
      countryCode
    }
    salon {
      id
      timezone
      branchName
      address
      city
      state
      email
      phone
      callingCode
      countryCode
      bearsProcessingFee
      isTaxVisible
      taxInclusivePricing
      locationType
      business {
        id
        name
        logoUrl
        headerImageUrl
        bookingUrlIdentifier
        cancellationPolicy
        priceHidden
        countryCode
        country {
          code
          currency {
            symbol
            code
            factor
          }
        }
      }
    }
  }
`;

const PAYMENT_AUTHORIZATION_FIELDS = `
  paymentAuthorization {
    accessCode
    authorizationUrl
    clientSecret
    reference
  }
`;

export const PUBLIC_APPOINTMENT_SUMMARY = `
  query PublicAppointmentSummary($bookingId: ID!) {
    appointmentSummary(bookingId: $bookingId) {
      amountToPay
      processingFeeAmount
      purpose
      message
      beautyNowAppUrl
      beautyNowAppCtaImageUrl
      ${APPOINTMENT_FIELDS}
      ${PAYMENT_AUTHORIZATION_FIELDS}
    }
  }
`;

export const INITIATE_APPOINTMENT_DEPOSIT_PAYMENT = `
  mutation InitiateAppointmentDepositPayment($input: InitiateAppointmentDepositPaymentInput!) {
    initiateAppointmentDepositPayment(input: $input) {
      amountToPay
      processingFeeAmount
      purpose
      message
      ${APPOINTMENT_FIELDS}
      ${PAYMENT_AUTHORIZATION_FIELDS}
    }
  }
`;

export const INITIATE_APPOINTMENT_FULL_PAYMENT = `
  mutation InitiateAppointmentFullPayment($input: InitiateAppointmentFullPaymentInput!) {
    initiateAppointmentFullPayment(input: $input) {
      amountToPay
      processingFeeAmount
      purpose
      message
      ${APPOINTMENT_FIELDS}
      ${PAYMENT_AUTHORIZATION_FIELDS}
    }
  }
`;

export const CONFIRM_STRIPE_APPOINTMENT_PAYMENT = `
  mutation ConfirmStripeAppointmentPayment($input: ConfirmStripeAppointmentPaymentInput!) {
    confirmStripeAppointmentPayment(input: $input) {
      status
      appointment {
        id
        bookingId
      }
      errors {
        message
        property
      }
    }
  }
`;

function firstErrorMessage(errors?: Array<{ message: string }>) {
  return errors?.[0]?.message || "Something went wrong";
}

export function usePublicAppointmentSummary(bookingId?: string) {
  const [data, setData] = useState<PublicAppointmentSummary | null>(null);
  const [loading, setLoading] = useState(Boolean(bookingId));
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!bookingId) return null;

    setLoading(true);
    setError(null);

    try {
      const response = await graphqlFetch<{
        appointmentSummary?: PublicAppointmentSummary | null;
      }>(PUBLIC_APPOINTMENT_SUMMARY, { bookingId });

      if (response.errors?.length) {
        throw new Error(firstErrorMessage(response.errors));
      }

      const summary = response.data?.appointmentSummary || null;
      setData(summary);
      return summary;
    } catch (caught) {
      const nextError = caught instanceof Error ? caught : new Error("Unable to load appointment");
      setError(nextError);
      return null;
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
}

export async function initiateAppointmentDepositPayment(input: PaymentInput) {
  const response = await graphqlFetch<{
    initiateAppointmentDepositPayment?: PublicAppointmentSummary | null;
  }>(INITIATE_APPOINTMENT_DEPOSIT_PAYMENT, { input });

  if (response.errors?.length) {
    throw new Error(firstErrorMessage(response.errors));
  }

  return response.data?.initiateAppointmentDepositPayment || null;
}

export async function initiateAppointmentFullPayment(input: PaymentInput) {
  const response = await graphqlFetch<{
    initiateAppointmentFullPayment?: PublicAppointmentSummary | null;
  }>(INITIATE_APPOINTMENT_FULL_PAYMENT, { input });

  if (response.errors?.length) {
    throw new Error(firstErrorMessage(response.errors));
  }

  return response.data?.initiateAppointmentFullPayment || null;
}

export async function confirmStripeAppointmentPayment(input: {
  appointmentId: string;
  reference: string;
}) {
  const response = await graphqlFetch<{
    confirmStripeAppointmentPayment?: {
      status?: number | null;
      appointment?: { id: string; bookingId?: string | null } | null;
      errors?: Array<{ message: string; property?: string | null }> | null;
    } | null;
  }>(CONFIRM_STRIPE_APPOINTMENT_PAYMENT, { input });

  if (response.errors?.length) {
    throw new Error(firstErrorMessage(response.errors));
  }

  const result = response.data?.confirmStripeAppointmentPayment;
  if (result?.status !== 200) {
    throw new Error(result?.errors?.[0]?.message || "Payment could not be verified");
  }

  return result;
}
